import { Injectable } from '@angular/core';
import { v4 as uuidv4, validate as uuidValidate  } from 'uuid';
import {cloneDeep} from 'lodash';

/**
 * Some of the code in this file references Knockout functions that have been set on window.
 * Here are the ones we know about:
 *
 window.LoggerFactory            = LoggerFactory;
 window.Debugger                 = Debugger;
 window.ErrorConsole             = PageViewModels.errorConsole;
 window.TableManager             = TableManager;
 window.TableRecord              = TableRecord;
 window.I18n                     = I18n;
 window.ClassUtil                = ClassUtil;
 window.Navigate                 = Navigate;
 window.observable               = ko.observable;
 window.observableArray          = ko.observableArray;
 window.computed                 = ko.computed;
 window.unwrapObservable         = ko.utils.unwrapObservable;
 window.isObservable             = ko.isObservable;
 window.isComputed               = ko.isComputed;
 window.isWritableObservable     = ko.isWritableObservable;
 window.Util                     = Util;
 window.Table                    = Table;
 window.DateTime                 = DateTime;
 window.User                     = User;
 window.dot                      = Util.dot;
 window.StringUtils              = StringUtils;
 window.ScriptLibrary            = ScriptLibrary;
 window.REST                     = WebsocketAPIImplementations.REST;
 window.WS                       = WebsocketAPIImplementations.WS;
 window.MessageBus               = MessageBus;
 window.RequestFactory           = RequestFactory;
 */

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  constructor() {
    (window as any).NG_SHARED_UTIL_SERVICE = this;
  }

  generateUUID(): string {
    return uuidv4();
  }

  validateUUID(value: string): boolean {
    return uuidValidate(this.dashifyUUID(value));
  }

  dashifyUUID(s: string): string {
    return (s == null || s.includes("-")) ? s : `${s.substr(0,8)}-${s.substr(8,4)}-${s.substr(12,4)}-${s.substr(16,4)}-${s.substr(20)}`;
  }

  // If the random id is going to be used as an HTML element id then it cannot start with a digit, hence use this method.
  generateCSSID() {
    return 'id-' +this.generateUUID();
  }

  validateCSSID(value: string): boolean {
    value = value ?? "";
    return value.startsWith("id-") && this.validateUUID(value.substring(3));
  }

  tick() {
    this.tickThen(() => {});
  }

  // tickThen(fn: () => any) {
  //   setTimeout(fn, 0);
  // }

  tickThen(fn: () => any, timeout = 0) {
    setTimeout(fn, timeout);
  }

  sleep(ms: number = 0) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  toBoolean(argument: any): boolean {
    if (argument === true || argument === false) return argument;

    switch(("" + argument).toLowerCase())
    {
      case "true":
      case "yes":
      case "1":
        return true;
      default:
        return false;
    }
  }

  toInteger(argument: any): number {
    return typeof argument === 'number' ? argument : parseInt("" + argument,10);
  }

  static isGUID(inputString: string): boolean {
    return inputString.match(/^[0-9a-f]{32}$/i) !== null ||
      inputString.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) !== null;
  };

  static isID(inputString: string): boolean {
    return this.isGUID(inputString);
  }

  static hasProperty(object: any, property: string): boolean {
    return object == null ? false : Object.prototype.hasOwnProperty.call(object, property);
  };

  static dot(startObject: any, dotWalkString?: string, defaultValue: any = undefined): any {
    let subElements: string[] = dotWalkString.split(".");
    let accum: any = this.isObservable(startObject) ? startObject(): startObject;
    let found = true;
    let subElementName;
    let subElementIsFunction: boolean = false;
    let i;
    let context;

    for (let i = 0; i < subElements.length; i++)
    {
      let subElementName: string = subElements[i];

      let subElementIsFunction: boolean = subElementName.includes("()");

      // See if the request wants the element evaluated
      if (subElementIsFunction)
      {
        subElementName = subElementName.slice( 0, subElementName.indexOf("()") );
      }

      if (accum != null && accum[subElementName] != null)
      {
        // save the context, as we need to pass the context to prototyped functions
        context = accum;

        accum = accum[subElementName];

        if (subElementIsFunction)
        {
          accum = accum.call(context);
        }
        else if (this.isObservable(accum))
        {
          accum = this.unwrapObservable(accum);
        }
      }
      else
      {
        found = false;
        break;
      }
    }

    if (found === true)
    {
      return accum;
    }
    else
    {
      return defaultValue;
    }
  }

  static evalWithArguments(code: string, args: object, thisContext?: any) {
    let argNames  = [];
    let argValues = [];

    args = args || {};
    thisContext = thisContext || null;

    Object.getOwnPropertyNames(args).forEach((name) => {
      argNames.push(name);
      argValues.push(args[name]);
    });

    const fun = new Function(argNames.join(","), code);

    return fun.apply(thisContext, argValues);
  }

  static isEmptyObject(obj) {
    for (let prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false;
      }
    }
    return true;
  }

  static isPlainObject(obj) {
    return Object.prototype.toString.call(obj) === '[object Object]';
  }

  static default(value, defaultValue) {
    return (value == null) ? defaultValue : value;
  };

  static defaultUndefined(value, defaultValue) {
    return (value === undefined) ? defaultValue : value;
  };

  static defaultBoolean(value, defaultValue?) {
    if (defaultValue === undefined) {
      defaultValue = false;
    }
    return (value == null) ? defaultValue : value == true || (String(value).toLowerCase() == "true");
  };

  static notImplemented(clazz: string, method: string) {
    console.debug(`${clazz}.${method} not implemented`);
  }

  static isObservable(object: any): boolean {
    return (window as any).isObservable(object);
  }

  static unwrapObservable(object: any): any {
    return (window as any).unwrapObservable(object);
  }

  omit = <T extends Object>(obj: T, keys: Set<string> | string[], visitedIn?: Set<any>): T => {
    if (Array.isArray(keys)) {
      keys = new Set(keys);
    }
    if (typeof obj !== 'object' || obj === null || obj === undefined) return obj;
    const visited = visitedIn ?? new Set();
    visited.add(obj);
    Object.entries(obj).forEach(([key, val]) => {
      if ((keys as Set<String>).has(key)) {
        delete obj[key as keyof T];
      } else if (typeof val === 'object' && !visited.has(val)) {
        this.omit(val, keys, visited);
      }
    });
    return obj;
  };

  clone(obj: any) {
    return cloneDeep(obj);
  }

  extractTextFromHtml(html: string) {
    let div = document.createElement('div');
    div.innerHTML = html
    return div.textContent || div.innerText
  }
}
